import React from 'react'
import { t } from 'ttag'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import { updateSelectedFilters } from 'store/actions/filters'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'

function SearchableMultiSelectTree({
  type,
  filterData,
  values,
  title,
  onClose,
  showFilter,
  keyWord,
  customTop
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]
  return (
    <PureSearchableMultiSelectTree
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      onClose={onClose}
      showFilter={showFilter}
      keyWord={keyWord}
      customTop={customTop}
      filterObject={filterObject}
    />
  )
}

const PureSearchableMultiSelectTree = React.memo(function PureSearchableMultiSelectTree({
  type,
  filterData,
  values,
  title,
  onClose,
  showFilter,
  keyWord,
  customTop,
  filterObject
}) {
  const selectedValues = filterObject[values[0]]
  const dispatch = useDispatch()
  const options = filterData.map(option => {
    const parentOption = {
      label: option[keyWord],
      value: option.id,
      expanded: true
    }
    const selectedOption = selectedValues.filter(
      s => s.id === option.id && s.label === option.content
    )
    if (selectedOption.length > 0) {
      parentOption.checked = true
    }
    parentOption.children = option.investment_priorities.map(o => {
      const childOption = {
        label: o[keyWord],
        value: o.id,
        expanded: true,
        parentId: option.id
      }
      if (parentOption.checked) {
        childOption.checked = true
      }
      selectedValues.forEach(sv => {
        if (sv.investment_priorities) {
          const selectedChildOption = sv.investment_priorities.filter(
            s => s.id === o.id && s.label === o[keyWord]
          )
          if (selectedChildOption.length > 0) {
            childOption.checked = true
          }
        }
      })
      return childOption
    })
    return parentOption
  })

  const onChange = (currentNode, selectedNodes) => {
    const filterValue = selectedNodes.map(node => {
      let value = {
        id: node.value,
        label: node.label
      }
      if (node.parentId) {
        value = {
          id: node.parentId,
          investment_priorities: [
            {
              id: node.value,
              label: node.label
            }
          ]
        }
      }
      return value
    })

    const data = {
      filterType: type,
      filterId: values[0],
      filterValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
    let resultingValue = null
    if (valueIsArray) {
      resultingValue = filterObject[filterId].filter(
        s => s.id !== filterValue.id || s !== filterValue
      )
    }
    const data = {
      filterType: type,
      filterId,
      filterValue: resultingValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject[value]) {
        const filterValue = filterObject[value]
        if (filterValue instanceof Array) {
          return filterValue.map(v => {
            if (v.investment_priorities) {
              return v.investment_priorities.map(ip => (
                <SelectedFilterContainer className="selected-filters" key={ip.id}>
                  <SelectedFilter>
                    <FilterText title={ip.label}>
                      <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{ip.label}</Dotdotdot>
                    </FilterText>
                    <RemoveIcon
                      className="far fa-times"
                      onClick={() => handleFilterRemove(value, v, true)}
                    />
                  </SelectedFilter>
                </SelectedFilterContainer>
              ))
            }
            return (
              <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
                <SelectedFilter>
                  <FilterText title={v.label ? v.label : v}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon
                    className="far fa-times"
                    onClick={() => handleFilterRemove(value, v, true)}
                  />
                </SelectedFilter>
              </SelectedFilterContainer>
            )
          })
        }
      }
      return false
    })
  }

  return (
    <>
      <SearchFilter open={showFilter} customTop={customTop}>
        <FilterTitle>{title}</FilterTitle>
        <CustomDropDownTreeSelect data={options} onChange={onChange} />
        <Button size="medium" onClick={onClose}>
          {t`Close`}
        </Button>
      </SearchFilter>
      <div>{renderSelectedValues()}</div>
    </>
  )
})

SearchableMultiSelectTree.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string
}

PureSearchableMultiSelectTree.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string,
  filterObject: PropTypes.object
}

export default SearchableMultiSelectTree
